// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import { reduxForm, Field, stopSubmit } from 'redux-form';
import { connect } from 'react-redux';
import { routerPush, isClient } from '@tcp/core/src/utils';
import validatorMethods from '@tcp/core/src/utils/formValidation/validatorMethods';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import TextBox from '@tcp/core/src/components/common/atoms/TextBox';
import Button from '@tcp/core/src/components/common/atoms/Button';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import RichText from '@tcp/core/src/components/common/atoms/RichText';
import SpinnerOverlay from '@tcp/core/src/components/common/atoms/SpinnerOverlay';
import createValidateMethod from '@tcp/core/src/utils/formValidation/createValidateMethod';
import getStandardConfig from '@tcp/core/src/utils/formValidation/validatorStandardConfig';
import Recaptcha from '@tcp/core/src/components/common/molecules/recaptcha/recaptcha';
import styles, { customSpinnerStyle } from '../styles/EmailForm.style';
import LoginTopSectionV2 from '../../LoginTopSectionV2';
import {
  getLabelFromCopyOrFromTranslation,
  getDynamicLabelValues,
} from '../../../../../../../utils/labels/labelUtilty';
import GlobalStoreLabels, {
  GLOBAL_STORE_LABELS,
} from '../../../../../../../utils/labels/globalStore.labels';
import {
  GLOBAL_LOGIN_LABELS,
  GlobalLoginLabels,
  lblLoginContinue,
  lblEnterValidEmail,
} from '../../../../../../../utils/labels/globalLogin.labels';
import {
  GLOBAL_GENERIC_LABELS,
  GlobalGenericLabels,
  lblEmailAddress,
} from '../../../../../../../utils/labels/globalGeneric.labels';
import { getExistingUserResponse, getLoginState } from '../../../container/LoginPage.selectors';

class EmailForm extends React.PureComponent {
  componentDidUpdate(prevProps) {
    // only run when existingUser actually changes
    if (
      prevProps.existingUser !== this.props.existingUser ||
      prevProps.error !== this.props.error
    ) {
      const { existingUser, dispatch, error } = this.props;
      const code = existingUser?.error?.errorCode || error?.get('errorCode');
      if (code === '_ERR_INVALID_EMAIL_DOMAIN') {
        dispatch(
          stopSubmit('LoginForm', {
            emailAddress: getLabelFromCopyOrFromTranslation(
              `${GLOBAL_LOGIN_LABELS}.${lblEnterValidEmail}`,
              GlobalLoginLabels
            ),
          })
        );
      } else {
        dispatch(stopSubmit('LoginForm', {}));
      }
    }
  }

  showForgotPasswordForm = (e) => {
    e.preventDefault();
    const {
      showForgotPasswordForm,
      resetForm,
      resetLoginState,
      closeModal,
      closeOverlay,
      isResetPassCodeValidationEnabled,
    } = this.props;
    resetForm();
    resetLoginState();
    if (isResetPassCodeValidationEnabled) {
      closeModal();
      closeOverlay();
      routerPush(`/change-password?page=reset`, `/change-password?page=reset`);
    } else {
      showForgotPasswordForm();
    }
  };

  resetError = () => {
    setTimeout(() => {
      const {
        resetLoginState,
        loginErrorMessage,
        otpLimit,
        resetOtpLimit,
        resetIsLocked,
        isLocked,
      } = this.props;
      if (loginErrorMessage) {
        resetLoginState();
      }
      if (otpLimit || isLocked) {
        resetOtpLimit();
        resetIsLocked();
      }
    }, 0);
  };

  updtateEmail = (event) => {
    setTimeout(() => {
      const { setUserEmailAddress } = this.props;
      const emailValidator = validatorMethods.validEmail;
      setUserEmailAddress('');
      if (
        event &&
        event.target &&
        event.target.id === 'emailAddress' &&
        emailValidator(event.target.value)
      ) {
        setUserEmailAddress(event.target.value);
      }
    }, 0);
  };

  loginLoader = () => {
    const { isLoading } = this.props;
    return <>{isLoading ? <SpinnerOverlay inheritedStyles={customSpinnerStyle} /> : null}</>;
  };

  focusFirstField = () => {
    const { fullPage } = this.props;
    if (isClient()) {
      return fullPage;
    }
    return false;
  };

  handleSubmitCustom = (e) => {
    e.preventDefault();
    const { handleSubmit } = this.props;
    return setTimeout(() => handleSubmit(e), 0);
  };

  render() {
    const {
      loginErrorMessage,
      className,
      showRecaptcha,
      change,
      pristine,
      isLoading,
      isLoginWithOTPEnabled,
      otpLimit,
      isLocked,
      lockTime,
      error,
    } = this.props;
    const labelGroup = getLabelFromCopyOrFromTranslation(GLOBAL_STORE_LABELS, GlobalStoreLabels);
    const shouldLoginButtonDisabled = () => pristine || isLoading;
    const { 'lbl_login_error_.MULTIPLE_OTP_ATTEMPT': lockedErrorMessage } =
      getLabelFromCopyOrFromTranslation(GLOBAL_LOGIN_LABELS, GlobalLoginLabels);

    const lockTimeValue = lockTime || '30';
    return (
      <div className={className}>
        {isLoginWithOTPEnabled && <LoginTopSectionV2 labelGroup={labelGroup} {...this.props} />}
        <form name="LoginForm" onSubmit={this.handleSubmitCustom} noValidate className={className}>
          {loginErrorMessage && error?.get('errorCode') !== '_ERR_INVALID_EMAIL_DOMAIN' && (
            <BodyCopy fontSize="fs12" fontWeight="semibold" color="red.500" className="elem-mb-XL">
              <RichText className="richTextColor" richTextHtml={loginErrorMessage} />
            </BodyCopy>
          )}
          {(otpLimit || isLocked) && !loginErrorMessage && (
            <BodyCopy fontSize="fs12" fontWeight="semibold" color="red.500" className="elem-mb-XL">
              {(otpLimit && isLocked) || otpLimit
                ? getDynamicLabelValues(
                    'dynamic.account_d.lbl_createAccount_error_ACCOUNT_VERIFY_OTP_COUNT_EXCEEDED'
                  )
                : lockedErrorMessage && lockedErrorMessage.replace('#lockTime', lockTimeValue)}
            </BodyCopy>
          )}
          <BodyCopy component="div" className="elem-mb-MED">
            <Field
              id="emailAddress"
              placeholder={getLabelFromCopyOrFromTranslation(
                `${GLOBAL_GENERIC_LABELS}.${lblEmailAddress}`,
                GlobalGenericLabels
              )}
              name="emailAddress"
              component={TextBox}
              dataLocator="login-emailfield"
              errorDataLocator="login-emailerror"
              showSuccessCheck={false}
              enableSuccessCheck={false}
              className="elem-mb-SM"
              onChange={this.resetError}
              onBlur={this.updtateEmail}
              onKeyDown={this.updtateEmail}
              autoFocus={this.focusFirstField()}
              spc
            />

            <BodyCopy component="div">
              {showRecaptcha && (
                <>
                  <Recaptcha
                    verifyCallback={(token) => change('recaptchaToken', token)}
                    expiredCallback={() => change('recaptchaToken', '')}
                  />
                  <Field
                    component={TextBox}
                    type="hidden"
                    name="recaptchaToken"
                    data-locator="login-recaptchcb"
                  />
                </>
              )}
            </BodyCopy>
          </BodyCopy>
          <BodyCopy component="div" textAlign="center" className="elem-mb-LRG">
            <Button
              fill="BLUE"
              type="submit"
              buttonVariation="fixed-width"
              dataLocator="login-logincta"
              fullWidth
              className={
                isLoginWithOTPEnabled
                  ? 'login-button-enlarge'
                  : 'elem-mb-XS login-button-test-class rounded-corner'
              }
              loading={isLoading}
              disabled={shouldLoginButtonDisabled()}
              showBorderRadiusStyle={isLoginWithOTPEnabled}
              spc
            >
              {getLabelFromCopyOrFromTranslation(
                `${GLOBAL_LOGIN_LABELS}.${lblLoginContinue}`,
                GlobalLoginLabels
              )}
            </Button>
          </BodyCopy>
        </form>
        {this.loginLoader}
      </div>
    );
  }
}

EmailForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  className: PropTypes.string,
  loginErrorMessage: PropTypes.string,
  showRecaptcha: PropTypes.bool,
  change: PropTypes.func,
  pristine: PropTypes.bool,
  showForgotPasswordForm: PropTypes.bool.isRequired,
  resetForm: PropTypes.bool.isRequired,
  resetLoginState: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  variation: PropTypes.string.isRequired,
  closeModal: PropTypes.func,
  closeOverlay: PropTypes.func,
  isResetPassCodeValidationEnabled: PropTypes.bool,
  fullPage: PropTypes.bool,
};

EmailForm.defaultProps = {
  className: '',
  loginErrorMessage: '',
  showRecaptcha: false,
  change: () => {},
  pristine: false,
  closeModal: () => {},
  closeOverlay: () => {},
  isResetPassCodeValidationEnabled: false,
  fullPage: false,
};

const validateMethod = createValidateMethod(
  getStandardConfig([
    { emailAddress: 'emailAddressNoAsync' },
    { password: 'legacyPassword' },
    'recaptchaToken',
  ])
);

const mapStateToProps = (state) => ({
  existingUser: getExistingUserResponse(state),
  error: getLoginState(state),
});

const StyledEmailForm = withStyles(EmailForm, styles);
const ConnectedEmailForm = connect(mapStateToProps)(StyledEmailForm);

export default reduxForm({
  form: 'LoginForm', // a unique identifier for this form
  enableReinitialize: true,
  ...validateMethod,
})(ConnectedEmailForm);

export { EmailForm as EmailFormFormVanilla };
